import { Box, Button, Card, CardActions, CardContent, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField, Typography } from '@mui/material';
import styles from './ProfileDetails.module.css';
import { memo, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {AppsOutlined, NumbersOutlined, CloseOutlined } from "@mui/icons-material";
import { IconButton as IconBtn } from 'components/buttons';
import { ConfirmationDialog } from 'components/dialogs';
import { Done, HideSource } from '@mui/icons-material';
import { TrialField } from 'components/forms';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Tabs } from 'components/layout';
import Apps from './Apps';
import Ranges from './Ranges';
import { resetAppsModesModel, resetRangesModesModel, setEditedApps, setEditedRanges, setSelected } from 'store/profile/profileSlice';
import { useDispatch, useSelector } from 'react-redux';
import { GridRowModes } from '@mui/x-data-grid';
import { useGetProfilesQuery } from 'services/data/profileApi';

const ProfileDetails = memo(({id, name, trial, licenceRequired, disabled, defaultMode, changeDefaultMode, 
                                startDefaultAppMode, editProfile, addProfile, apps, ranges, resetFitersAndSort}) => {

  const {data: modesData, isError: isModesError, error: modesError} = useGetProfilesQuery({mode: 'appModes'});

  const [currentName, setCurrentName] = useState(name ?? `Профиль от ${new Date().toLocaleDateString()}`);
  const [currentTrial, setCurrentTrial] = useState(trial ?? '');
  const [currentLicence, setCurrentLicence] = useState(licenceRequired ?? false);
  const [currentDisabled, setCurrentDisabled] = useState(disabled ?? false);
  const [flush, setFlush] = useState(false);
  const {selected, appsModesModel, rangesModesModel, editedApps, editedRanges} = useSelector(state => state.profile);
  const dispatch = useDispatch();  

  const appsRef = useRef(null);
  const rangesRef = useRef(null);


  
  useEffect(() => { 
    setCurrentName(name ?? `Профиль от ${new Date().toLocaleDateString()}`);
    setCurrentTrial(trial ?? ''); 
    setCurrentLicence(licenceRequired ?? false);
    setCurrentDisabled(disabled ?? false);
  }, [name, trial, licenceRequired, disabled]);

  useEffect(() => { 
    dispatch(setEditedApps([]));
    dispatch(setEditedRanges([]));
  }, [selected]);

  useEffect( () => {
    return () => {
      dispatch(resetAppsModesModel());
      dispatch(resetRangesModesModel());
    }
  }, []);
 
  
  const [open, setOpen] = useState(false);
  
  const changeDisabled = (e) => {
    setCurrentDisabled((oldValue) => !oldValue);
    setOpen(false);
  };
  const cancelHandler = (e) => setOpen(false);  
  
  const isAddingMode = () =>  !id;
  const getSaveButtonText = () => id ? 'Изменить профиль' : 'Добавить новый профиль';

 
  const getDisableButtonIcon = () => currentDisabled ? <Done fontSize='small'/> : <HideSource fontSize='small'/>;
  const isSaveButtonDisabled = () => !isValid();
  
  const isValid = () => {
    if(!currentName) return false;
    
    const isEditedApps = Object.values(appsModesModel).some(item => item.mode === GridRowModes.Edit);
    const isEditedRanges = Object.values(rangesModesModel).some(item => item.mode === GridRowModes.Edit);
    
    if(!isAddingMode()) { 
      return !(name===currentName && trial===currentTrial && licenceRequired===currentLicence && disabled===currentDisabled &&
        defaultMode===startDefaultAppMode && !editedApps.length && !editedRanges.length && !isEditedApps && !isEditedRanges);
    }
    return true;
  };
  
 
  const makeBody = () => {
   
    const result = {body: {}};
    id && (result.id = id);
    
    editedRanges.length && (result.body.ranges = editedRanges); 
    editedApps.length && (result.body.apps = editedApps.map(({id, ...others}) => ({package: id, ...others}))); 
    
    if(name != currentName) result.body.name = currentName;
    if(trial != currentTrial) result.body.trial = currentTrial;
    if(licenceRequired != currentLicence) result.body.licenceRequired = currentLicence;
    if(disabled != currentDisabled) result.body.disabled = currentDisabled;
    if(defaultMode != startDefaultAppMode) result.body.default = defaultMode;
    
    return result;      
  };

  const flushData = useCallback(async (type, data) => {
      setFlush(false);

      const body = makeBody();
            
      if(data && data.length) {
        body.body = {...body.body, [type]: data};
      }

      if(isAddingMode()) {
        if(!body.body.apps) body.body.apps = [];

        body.body.apps = body.body.apps.reduce((acc, current) => {
          const idx = acc.findIndex(el => el.package === current.package);
          if(idx != -1) {
            const editedItem = {...acc[idx], ...current};
            return [...acc.slice(0, idx), editedItem, ...acc.slice(idx + 1)];
          }

        }, apps.map(item => ({package: item.id, version: item.version, mode: item.mode})));
      }
      
      dispatch(resetRangesModesModel()); 
      dispatch(resetAppsModesModel());
      
      const res = isAddingMode() ? addProfile(body) : editProfile(body);
      
      res
        .then((payload) => { 
          dispatch(setEditedRanges([])); 
          dispatch(setEditedApps([]));          
          dispatch(setSelected({id: (id ?? payload.body.id), disabled: currentDisabled, trial: currentTrial, name: currentName,
                                 licenceRequired: currentLicence}));
          if(isAddingMode()) {
            resetFitersAndSort();
          }                        
        })
        .catch(error => {
          console.log('error ', error);
        });
      

    }, [makeBody]);

  const makeItems = useCallback((items, editedItems) => { 
    if(!items) return [];
    if(!editedItems || !editedItems.length) return items;

    const newItems = editedItems.reduce((acc, current) => {
      const idx = acc.findIndex(el => el.id === current.id); 
      if(idx === -1) {
        return [current, ...acc];
      }
      const editedItem = {...acc[idx], ...current};
      return [...acc.slice(0, idx), editedItem, ...acc.slice(idx + 1)];
    }, items);

    
    return newItems;
  }, [selected]);

  return (
    <div className={styles.details}>
      <div className={styles.content}>
        <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
          <CardContent sx={{overflow: 'auto'}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2}}>
              <TextField
                fullWidth
                size='small'
                label='Наименование профиля'
                value={currentName}
                InputProps={{
                  endAdornment: currentName && (
                    <IconButton onClick={()=>setCurrentName('')}>
                      <CloseOutlined/>
                    </IconButton>
                  )
                }}
                onChange={(e) => setCurrentName(e.target.value)}
              />
              <IconBtn
                className={styles.disableButton}
                size='small'
                handler={() => setOpen(true)}
                text={currentDisabled ? 'Разрешить профиль' : 'Запретить профиль'}
                variant="outlined"  
                iconCmp={getDisableButtonIcon()}
                disabled={isAddingMode()}
              />
              <ConfirmationDialog 
                title={currentDisabled ? 'Разрешить профиль' : 'Запретить профиль'}
                text={<span>{currentDisabled ? 'Разрешить диапазон' : 'Запретить профиль'} <b>{name}</b>? {currentDisabled ? 'Профиль станет доступным для использования в кассах.' : 'Профиль станет недоступным для использования в кассах.'}</span>}
                open={open}
                onOk={changeDisabled}
                onCancel={cancelHandler}
              />
            </Box>
            <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', width: '100%', overflow: 'hidden'}}>
              <Typography className="label">Триальный период для работы профиля</Typography>
              <TextField 
                label='Количество дней'
                size='small'
                InputLabelProps={{
                    shrink: true
                }}
                placeholder='Запрещён'
                value={currentTrial} 
                inputProps={{style: {textAlign: 'center'}}}
                InputProps={{
                  inputComponent: TrialField
                }}
                onChange={(value) => setCurrentTrial(value ?? '')}
                sx={{my:1, width: '200px'}}
              />
            </Stack>  
            <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', width: '100%', overflow: 'hidden'}}>
              <Typography className="label">Для работы профиля требуется лицензия</Typography>
              <Switch checked={currentLicence} onChange={(event) => setCurrentLicence(event.target.checked)}/>                            
            </Stack>
            <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', width: '100%', overflow: 'hidden'}}>
              <Typography className="label">Значение по-умолчанию для всех приложений профиля</Typography>
              <FormControl>
                <Select 
                  size='small'
                  value={defaultMode}
                  onChange={(e) => {
                    changeDefaultMode(e.target.value)
                  }}
                  sx={{my:1, width: '200px'}}
                >
                    {
                      modesData?.body?.items?.filter(el => el?.profileVisible === true).map(item => (
                        <MenuItem key={item.id} value={item.id}>
                              {item.value}
                        </MenuItem>
                      ))
                    }
                </Select>
              </FormControl>                          
            </Stack>
            <Tabs>              
              <Apps key='apps' value='apps' title='Приложения' ref={appsRef} flush={flush} flushData={flushData} icon={<AppsOutlined/>}
                  items={makeItems(apps, editedApps)} modesData={modesData} />
              <Ranges key='ranges' value='ranges' title='Заводские номера' ref={rangesRef} flush={flush} flushData={flushData} icon={<NumbersOutlined/>}
                  items={makeItems(ranges, editedRanges)} />
            </Tabs>
          </CardContent>
          <CardActions sx={{px: 2}}>
            <Button
              fullWidth
              disabled={isSaveButtonDisabled()}
              startIcon={<SaveOutlinedIcon/>}
              color='error'
              variant='contained'
              sx={{my: 1}}
              onClick={() => setFlush(true)}
            >{getSaveButtonText()}</Button>    
          </CardActions>
        </Card>
      </div>
    </div>
  );
});

export default ProfileDetails;